/**
 * Schemas for common environment concepts.
 * DO NOT include app specific environment variable schemas here.
 */

import { z } from "zod/mini";

/**
 * Environment variable sensitivity classification.
 * Used as suffix in env var names: `VAR_NAME__SAFE`, `VAR_NAME__PROTECTED`, `VAR_NAME__RADIOACTIVE`
 *
 * - SAFE: Public information, can be logged/exposed in frontend
 * - PROTECTED: Internal details, shouldn't be casually exposed
 * - RADIOACTIVE: Secrets/credentials, must never be logged or exposed
 */
export type Sensitivity = "SAFE" | "PROTECTED" | "RADIOACTIVE";

/** Helper to make a schema nullable via union with z.null() */
export const Nullable = <T extends z.ZodMiniType>(schema: T) =>
  z.union([schema, z.null()]);

export const DeployEnvironmentSchema = z.enum([
  "development",
  "production",
  "pr-preview",
]);
export type DeployEnvironment = z.infer<typeof DeployEnvironmentSchema>;

export const GitShaSchema = z
  .string()
  .check(
    z.regex(
      /^[a-f0-9]{40}$/i,
      "Must be a valid full-length Git SHA (40 hex chars)"
    )
  );

export const PortSchema = z
  .number()
  .check(z.int(), z.positive())
  .check(
    z.refine(
      (n) => n >= 1 && n <= 65_535,
      "Port must be an integer between 1 and 65535"
    )
  );

const DATABASE_URL_REGEX = /^(\w+):\/\/.+/;

export const DatabaseUrlSchema = z
  .string()
  .check(
    z.refine(
      (s) => DATABASE_URL_REGEX.test(s),
      "Must be a valid database connection URL (e.g. protocol://user:pass@host:port/db)"
    )
  );

export const SentrySchema = z.object({
  DSN: z.string().check(z.minLength(1), z.url()),
});

export const PostHogSchema = z.object({
  KEY: z.string().check(z.minLength(1)),
  HOST: z.string().check(z.url()),
});

export const OtelExportFormatSchema = z.enum(["json", "protobuf"]);
export type OtelExportFormat = z.infer<typeof OtelExportFormatSchema>;

export const OtelSchema = z.object({
  ENDPOINT: z.string().check(z.url()),
  HEADERS_JSON_SERIALIZED: z.string().check(
    z.refine((s) => {
      try {
        const val = JSON.parse(s);
        return typeof val === "object" && val !== null && !Array.isArray(val);
      } catch {
        return false;
      }
    }, "Must be a valid JSON object string")
  ),
  EXPORT_FORMAT: OtelExportFormatSchema,
});
